import { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { AmtTaskDisplay } from '../../utils/amtModdleDescriptor';
import { PRIORITY_OPTIONS, createEmptyTaskDisplay } from '../../utils/amtModdleDescriptor';

interface TaskDisplayEditorProps {
  value: AmtTaskDisplay | null;
  onChange: (value: AmtTaskDisplay) => void;
}

const SUPPORTED_LANGUAGES = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];

export function TaskDisplayEditor({ value, onChange }: TaskDisplayEditorProps) {
  const taskDisplay = value || createEmptyTaskDisplay();
  const [expanded, setExpanded] = useState(true);

  const handleLabelChange = useCallback(
    (lang: string, text: string) => {
      onChange({
        ...taskDisplay,
        labels: { ...taskDisplay.labels, [lang]: text },
      });
    },
    [taskDisplay, onChange]
  );

  const handleDescriptionChange = useCallback(
    (lang: string, text: string) => {
      onChange({
        ...taskDisplay,
        descriptions: { ...taskDisplay.descriptions, [lang]: text },
      });
    },
    [taskDisplay, onChange]
  );

  const handlePriorityChange = useCallback(
    (priority: AmtTaskDisplay['priority']) => {
      onChange({ ...taskDisplay, priority });
    },
    [taskDisplay, onChange]
  );

  const handleIconChange = useCallback(
    (icon: string) => {
      onChange({ ...taskDisplay, icon: icon || undefined });
    },
    [taskDisplay, onChange]
  );

  const handleColorChange = useCallback(
    (color: string) => {
      onChange({ ...taskDisplay, color: color || undefined });
    },
    [taskDisplay, onChange]
  );

  const addLanguage = useCallback(
    (lang: string) => {
      if (!taskDisplay.labels[lang]) {
        onChange({
          ...taskDisplay,
          labels: { ...taskDisplay.labels, [lang]: '' },
          descriptions: { ...taskDisplay.descriptions, [lang]: '' },
        });
      }
    },
    [taskDisplay, onChange]
  );

  const removeLanguage = useCallback(
    (lang: string) => {
      if (lang === 'de') return; // Don't remove German
      const newLabels = { ...taskDisplay.labels };
      const newDescriptions = { ...taskDisplay.descriptions };
      delete newLabels[lang];
      delete newDescriptions[lang];
      onChange({
        ...taskDisplay,
        labels: newLabels,
        descriptions: newDescriptions,
      });
    },
    [taskDisplay, onChange]
  );

  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    (lang) => !Object.hasOwn(taskDisplay.labels, lang.code)
  );

  const currentPriority = PRIORITY_OPTIONS.find((p) => p.value === taskDisplay.priority);

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2">Task-Anzeige</Typography>
          {currentPriority && (
            <Chip
              label={currentPriority.label}
              size="small"
              sx={{ bgcolor: currentPriority.color, color: 'white' }}
            />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Labels for each language */}
          {Object.entries(taskDisplay.labels).map(([lang, text]) => (
            <Box key={lang} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Chip
                label={SUPPORTED_LANGUAGES.find((l) => l.code === lang)?.label || lang}
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
              />
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Bezeichnung"
                  value={text}
                  onChange={(e) => handleLabelChange(lang, e.target.value)}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Beschreibung"
                  multiline
                  rows={2}
                  value={taskDisplay.descriptions[lang] || ''}
                  onChange={(e) => handleDescriptionChange(lang, e.target.value)}
                  sx={{ mt: 1 }}
                />
              </Box>
              {lang !== 'de' && (
                <IconButton size="small" onClick={() => removeLanguage(lang)} sx={{ mt: 1 }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}

          {/* Add language button */}
          {availableLanguages.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {availableLanguages.map((lang) => (
                <Chip
                  key={lang.code}
                  label={`+ ${lang.label}`}
                  size="small"
                  variant="outlined"
                  onClick={() => addLanguage(lang.code)}
                  icon={<AddIcon />}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          )}

          {/* Priority */}
          <FormControl size="small" fullWidth>
            <InputLabel>Prioritaet</InputLabel>
            <Select
              value={taskDisplay.priority}
              label="Prioritaet"
              onChange={(e) => handlePriorityChange(e.target.value as AmtTaskDisplay['priority'])}
            >
              {PRIORITY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: option.color,
                      }}
                    />
                    {option.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Icon and Color */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size="small"
              label="Icon (MUI Icon Name)"
              value={taskDisplay.icon || ''}
              onChange={(e) => handleIconChange(e.target.value)}
              placeholder="z.B. Assignment"
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label="Farbe"
              type="color"
              value={taskDisplay.color || '#1976d2'}
              onChange={(e) => handleColorChange(e.target.value)}
              sx={{ width: 100 }}
              InputProps={{
                sx: { height: 40 },
              }}
            />
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
